import BgBaseCard from '#/components/bg-base-card';
import { Button } from '#/components/ui/button';
import { Switch } from '#/components/ui/switch';
import { Label } from '#/components/ui/label';
import TextsArea from '../../components/text-area';
import SelectTexts from '../../components/select-texts';
import { SelectItemType } from 'types/common';
import { spellerLength } from '#/data/constants';
import { trackEvent } from '#/lib/analytics';
import { Dispatch, SetStateAction } from 'react';
import AnonUsageBadge from '../../components/anon-usage-badge';
import PlanUsageBadge from '../../components/plan-usage-badge';
import type { PlanLimits } from '#/types/plan-limits';
import { Settings2, Zap } from 'lucide-react';

interface TextInputSectionProps {
  isUser: boolean;
  text: string;
  setText: Dispatch<SetStateAction<string>>;
  localTexts: SelectItemType[];
  setLocalTexts: (texts: SelectItemType[]) => void;
  onAnalyze: () => void;
  onDictSettings: () => void;
  anonRemaining?: number | null;
  planLimits?: PlanLimits | null;
  autoJointing: boolean;
  autoSpacing: boolean;
  autoSplitSentence: boolean;
  withSense: boolean;
  raw: boolean;
  setAutoJointing: (v: boolean) => void;
  setAutoSpacing: (v: boolean) => void;
  setAutoSplitSentence: (v: boolean) => void;
  setWithSense: (v: boolean) => void;
  setRaw: (v: boolean) => void;
}

export default function AnalyzeTextInputSection({
  isUser,
  text,
  setText,
  localTexts,
  setLocalTexts,
  onAnalyze,
  onDictSettings,
  anonRemaining,
  planLimits,
  autoJointing,
  autoSpacing,
  autoSplitSentence,
  withSense,
  raw,
  setAutoJointing,
  setAutoSpacing,
  setAutoSplitSentence,
  setWithSense,
  setRaw
}: TextInputSectionProps) {
  /**
   * 분석 옵션 토글을 GA4 로 남긴다. 의미 구분(WSD)·원형 분석 같은 기능이 실제로
   * 쓰이는지, 아니면 아무도 켜지 않는지를 확인하려는 목적이다. 켬/끔을 모두
   * 보내되 enabled 로 구분한다.
   */
  const trackToggle =
    (option: string, setter: (v: boolean) => void) => (next: boolean) => {
      trackEvent('toggle_option', {
        tool: 'analyze',
        option,
        enabled: next
      });
      setter(next);
    };

  const isTextTooLong = !isUser && text.length > spellerLength;
  const isQuotaExhausted =
    isUser &&
    planLimits &&
    !planLimits.isAdmin &&
    planLimits.usage.eojeolRemaining !== null &&
    planLimits.usage.eojeolRemaining <= 0 &&
    (planLimits.usage.onetimeEojeolBalance ?? 0) <= 0;

  const canAnalyze =
    text.trim().length > 0 &&
    !isTextTooLong &&
    !(!isUser && anonRemaining === 0) &&
    !isQuotaExhausted;

  return (
    <>
      <div className="flex flex-col gap-4">
        <SelectTexts
          localTexts={localTexts}
          setLocalTexts={setLocalTexts}
          text={text}
          setText={setText}
        />
      </div>

      <BgBaseCard>
        <div className="flex flex-col h-full gap-4">
          <div className="flex gap-4 flex-wrap">
            <div className="flex gap-2 items-center">
              <Switch
                size="sm"
                id="autoJointing"
                checked={autoJointing}
                onCheckedChange={trackToggle('auto_jointing', setAutoJointing)}
                disabled={
                  raw ||
                  (isUser && planLimits ? !planLimits.features.autoJoin : false)
                }
              />
              <Label
                htmlFor="autoJointing"
                className={
                  raw || (isUser && planLimits && !planLimits.features.autoJoin)
                    ? 'text-muted-foreground'
                    : ''
                }
              >
                자동 붙여쓰기
                {isUser &&
                  planLimits &&
                  !planLimits.features.autoJoin &&
                  ' (Standard+)'}
              </Label>
            </div>
            <div className="flex gap-2 items-center">
              <Switch
                size="sm"
                id="autoSpacing"
                checked={autoSpacing}
                onCheckedChange={trackToggle('auto_spacing', setAutoSpacing)}
                disabled={raw}
              />
              <Label
                htmlFor="autoSpacing"
                className={raw ? 'text-muted-foreground' : ''}
              >
                자동 띄어쓰기
              </Label>
            </div>
            <div className="flex gap-2 items-center">
              <Switch
                size="sm"
                id="autoSplitSentence"
                checked={autoSplitSentence}
                onCheckedChange={trackToggle(
                  'auto_split_sentence',
                  setAutoSplitSentence
                )}
                disabled={
                  isUser && planLimits
                    ? !planLimits.features.autoSentenceSplit
                    : false
                }
              />
              <Label
                htmlFor="autoSplitSentence"
                className={
                  isUser && planLimits && !planLimits.features.autoSentenceSplit
                    ? 'text-muted-foreground'
                    : ''
                }
              >
                자동 문장분리
                {isUser &&
                  planLimits &&
                  !planLimits.features.autoSentenceSplit &&
                  ' (Standard+)'}
              </Label>
            </div>

            {/* WSD — 동형이의어(같은 표기, 다른 뜻)에 어깨번호·뜻풀이를 붙인다. */}
            <div className="flex gap-2 items-center">
              <Switch
                size="sm"
                id="withSense"
                checked={withSense}
                onCheckedChange={trackToggle('with_sense', setWithSense)}
              />
              <Label htmlFor="withSense" className="gap-1.5">
                의미 구분
                <span className="text-[10px] font-medium text-primary/70 border border-primary/30 rounded px-1">
                  BETA
                </span>
              </Label>
            </div>

            {/* Raw — 후처리를 끈 순수 모델 출력(AnalyzeSyntaxRaw) */}
            <div className="flex gap-2 items-center">
              <Switch
                size="sm"
                id="raw"
                checked={raw}
                onCheckedChange={trackToggle('raw', setRaw)}
              />
              <Label htmlFor="raw">원형 분석(Raw)</Label>
            </div>
          </div>

          {raw && (
            <p className="text-xs text-muted-foreground">
              원형 분석은 모델 출력을 그대로 보여줍니다. 복합명사·복합동사
              분해와 자동 띄어쓰기·붙여쓰기를 적용하지 않고 사용자 사전도
              반영하지 않습니다.
            </p>
          )}

          <TextsArea isUser={isUser} text={text} setText={setText} autoFocus />

          <div className="flex gap-3 flex-wrap items-center justify-between">
            <div className="flex items-center gap-3">
              {!isUser && <AnonUsageBadge remaining={anonRemaining} />}
              {isUser && planLimits && (
                <PlanUsageBadge planLimits={planLimits} />
              )}
              {!isUser && (
                <span className="text-xs text-muted-foreground">
                  {text.length.toLocaleString()} /{' '}
                  {spellerLength.toLocaleString()}자
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {isUser && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDictSettings}
                  className="gap-1.5"
                  // 원형 분석은 사용자 사전을 반영하지 않는다.
                  disabled={raw}
                  title={
                    raw
                      ? '원형 분석에서는 사용자 사전이 적용되지 않습니다.'
                      : ''
                  }
                >
                  <Settings2 className="size-3.5" />
                  사전 설정
                </Button>
              )}
              <Button
                disabled={!canAnalyze}
                onClick={onAnalyze}
                className="gap-1.5"
              >
                <Zap className="size-4" />
                분석
              </Button>
            </div>
          </div>
        </div>
      </BgBaseCard>
    </>
  );
}
